<script setup lang="ts">
import { computed } from "vue"
import { useRoute } from 'vue-router'
import { formatHTML, publicDomain, brandLogoUrl } from '~/helper'
import { useTripPlanStore } from '~/store/pinia/tripPlan'

const route = useRoute()
const appConfig = useAppConfig()
const runtimeConfig = useRuntimeConfig()
const domain = publicDomain()
const BRAND_LOGO_URL = brandLogoUrl()
const tripPlanStore = useTripPlanStore()

const dataTrip = computed(() => tripPlanStore.dataTrip)
const priceDisplay = computed(() => tripPlanStore.priceDisplayAPI)
const priceValidUntil = computed(() => {
  const date = new Date(new Date().setFullYear(new Date().getFullYear() + 1))
  return date.toISOString().split('T')[0]
})
const baseUrl = computed(() => {
  if (domain === 'agent') {
    return appConfig.publicDomainAgent
  }

  if (domain === 'agentpreview') {
    return appConfig.publicDomainAgentPreview
  }

  if (domain === 'preview') {
    return appConfig.publicDomainPreview
  } else {
    return runtimeConfig.public.publicDomain
  }
})
const imageList = computed(() => {
  const imgArr = dataTrip.value && dataTrip.value.images
  const imgList =
    imgArr &&
    imgArr.map((v: any) => {
      return v.url
    })
  return imgList
})
const reviewList = computed(() => {
  const reviewArr = dataTrip.value && dataTrip.value?.rating_list
  const reviewList =
    reviewArr &&
    reviewArr.map((v: any) => {
      return {
        '@type': v.type,
        author: {
          '@type': 'Person',
          name: v.author,
        },
        datePublished: v.date_published,
        reviewBody: v.review_body,
        reviewRating: {
          '@type': 'Rating',
          bestRating: v.review_rating.best_rating,
          ratingValue: v.review_rating.rating_value,
          worstRating: v.review_rating.worst_rating,
        },
      }
    })
  return reviewList
})
const sku = computed(() => {
  const str = route.path
  const result = str.split('-').pop()
  return result
})
const tripLdJson = computed(() => {
  const timeOneday = 24 * 60 * 60 * 1000
  const today = new Date()
  const arrivalTime = new Date(today.getTime() + 90 * timeOneday)
  const priceValidUntil = new Date(today.getTime() + 14 * timeOneday)
  const departureTime = new Date(arrivalTime.getTime() + dataTrip.value?.itinerary?.length * timeOneday)
  return {
    '@context': 'https://schema.org',
    '@type': 'Trip',
    name: dataTrip.value?.meta_title || dataTrip.value?.name,
    description: dataTrip.value?.meta_description || formatHTML(dataTrip.value?.highlights?.join(' '), 155) || formatHTML(dataTrip.value?.overview, 155),
    arrivalTime,
    departureTime,
    itinerary: {
      '@type': 'ItemList',
      numberOfItems: dataTrip.value?.itinerary?.length,
      itemListElement: dataTrip.value?.itinerary?.map((item: any, index: number) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'TouristAttraction',
            name: item?.title,
            description: item?.overview,
          },
        }
      }),
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'AUD',
      price: dataTrip?.value?.converted_budget_per_traveller || priceDisplay?.value?.tracking?.converted_perPerson,
      availability: 'https://schema.org/InStock',
      priceValidUntil,
      url: `${baseUrl.value}${dataTrip.value && dataTrip.value?.breadcrumb && dataTrip.value?.breadcrumb[1].link}`,
    },
    potentialAction: [
      {
        '@type': 'AskAction',
        name: 'Design your trip',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseUrl.value}${dataTrip.value && dataTrip.value?.breadcrumb && dataTrip.value?.breadcrumb[1].link}/design`,
          inLanguage: 'en-US',
          actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform'],
        },
      },
    ],
  }
})
</script>

<template>
  <template v-if="dataTrip && priceDisplay">
    <Teleport to="head">
      <component :is="'script'" type="application/ld+json">
        { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement":[ { "@type": "ListItem", "position": 1, "item": { "@id": "{{
          baseUrl
        }}", "name": "Home"} }, { "@type": "ListItem", "position": 2, "item": { "@id": "{{ baseUrl
        }}{{ dataTrip && dataTrip.breadcrumb && dataTrip.breadcrumb[0].link }}", "name": "{{ dataTrip && dataTrip.breadcrumb && dataTrip.breadcrumb[0].title }}"
        } } , { "@type": "ListItem", "position": 3, "item": { "@id": "{{ baseUrl }}{{ dataTrip && dataTrip.breadcrumb && dataTrip.breadcrumb[1].link }}",
        "name": "{{ dataTrip && dataTrip.breadcrumb && dataTrip.breadcrumb[1].title }}" } } ] }
      </component>

      <component :is="'script'" type="application/ld+json">
        { "@context":"https://schema.org", "@type":"TravelAgency", "url":"https://www.designerjourneys.com", "name": "Designer Journeys",
        "image":"{{ BRAND_LOGO_URL }}", "address": { "@type": "PostalAddress", "streetAddress": "4/12
        Elizabeth Street", "addressLocality": "Hawthorn", "addressRegion": "VIC", "postalCode": "3122", "addressCountry": "Australia" }, "telephone": "+61 3
        9071 0069", "priceRange": "$1,000 - $10,000" }
      </component>
      <component :is="'script'" type="application/ld+json">
        { "@context": "https://schema.org", "@type": "Organization", "url": "https://www.designerjourneys.com", "name": "Designer Journeys", "sameAs":
        ["https://www.facebook.com/DesignerJourneys","https://www.instagram.com/designerjourneys_/","https://www.pinterest.com.au/designerjourneys/","https://www.linkedin.com/company/designerjourneys/"],
        "logo": "{{ BRAND_LOGO_URL }}" }
      </component>
      <component :is="'script'" type="application/ld+json">
        { "@context":"https://schema.org", "@type":"DepartAction", "object": { "@type":"country", "name":"{{ dataTrip && dataTrip.start_country }} ,
        {{ dataTrip && dataTrip.start_city }}" } }
      </component>
      <component :is="'script'" type="application/ld+json">
        { "@context":"https://schema.org", "@type":"ArriveAction", "object": { "@type":"country", "name":"{{ dataTrip && dataTrip.end_country }} ,
        {{ dataTrip && dataTrip.end_city }}" } }
      </component>

      <component :is="'script'" type="application/ld+json">
        { "@context": "https://schema.org", "@type": "Product", "@id": "{{ baseUrl }}{{ dataTrip && dataTrip.breadcrumb && dataTrip.breadcrumb[1].link }}",
        "additionalType": "https://www.productontology.org/id/Excursion", "name": "{{ dataTrip && dataTrip.name }}", "description":
        {{ dataTrip && JSON.stringify(dataTrip.overview, ' \\" ', ' ') }}, "aggregateRating": { "@type": "AggregateRating", "ratingValue": "{{
          dataTrip && dataTrip.designer && dataTrip.designer.rating
        }}", "reviewCount": "{{ dataTrip && dataTrip.rating_list && dataTrip.rating_list.length > 0 ? dataTrip.rating_list.length : 1 }}" }, "offers": {
        "@type": "Offer", "priceCurrency": "AUD", "price": "{{
          dataTrip?.converted_budget_per_traveller || (priceDisplay && priceDisplay?.tracking?.converted_perPerson)
        }}", "availability": "https://schema.org/InStock", "priceValidUntil": "{{ priceValidUntil }}", "url" : "{{ baseUrl
        }}{{ dataTrip && dataTrip.breadcrumb && dataTrip.breadcrumb[1].link }}" }, "brand": { "@type": "Brand", "name": "Designer Journeys" }, "mpn": "{{
          dataTrip && dataTrip.id
        }}", "image": {{ imageList }}, "review" :{{ reviewList }}, "sku": "{{ sku }}", "potentialAction": [ { "@type": "AskAction", "name": "Design your trip",
        "target": { "@type": "EntryPoint", "urlTemplate": "https://www.designerjourneys.com/design", "inLanguage": "en-US", "actionPlatform": [
        "http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform" ] } } ] }
      </component>
      <component :is="'script'" type="application/ld+json">
        {{ JSON.stringify(tripLdJson) }}
      </component>
    </Teleport>
  </template>
</template>

<style lang="scss" scoped></style>
